/**
 * OrderHistoryList — the customer-facing list of placed orders.
 *
 * Shared by the account dashboard's "Recent Orders" panel and the dedicated
 * /account/orders page. Extracted rather than copied: both surfaces read the same
 * orders-store and have to agree about status, totals and payment method, and two
 * copies of this markup would drift the first time an order field changes.
 */

import { Link } from "@tanstack/react-router";
import { ArrowRight, ShoppingBag, MessageCircle } from "lucide-react";
import { formatPrice } from "@/components/forms/price-display";
import type { UserOrder } from "@/features/orders/store/orders-store";

type OrderHistoryListProps = {
  orders: UserOrder[];
};

export function OrderHistoryList({ orders }: OrderHistoryListProps) {
  if (orders.length === 0) {
    return (
      <div className="py-12 text-center">
        <ShoppingBag className="mx-auto h-10 w-10 text-muted-foreground/60" strokeWidth={1.25} />
        <p className="mt-3 font-display text-base">No orders placed yet</p>
        <p className="mt-1 text-xs text-muted-foreground">
          When you purchase items, your order history and tracking updates will appear here.
        </p>
        <Link
          to="/shop"
          className="mt-5 inline-flex min-h-11 items-center gap-2 bg-primary px-6 text-xs uppercase tracking-wider text-primary-foreground hover:opacity-90"
        >
          Explore Catalogue <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <ul className="divide-y divide-border/80 mt-4">
      {orders.map((order) => (
        <li
          key={order.id}
          className="py-5 flex flex-col gap-4"
        >
          {/* Order Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/50 pb-3">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs font-bold text-foreground">{order.id}</span>
              <span className="inline-block rounded-full bg-emerald-100 dark:bg-emerald-950 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 dark:text-emerald-300">
                {order.status}
              </span>
            </div>

            <div className="flex items-center gap-4 text-xs">
              <span className="text-muted-foreground">Placed on {order.date}</span>
              <span className="font-display font-bold text-base text-foreground tabular-nums">
                {formatPrice(order.total)}
              </span>
            </div>
          </div>

          {/* Items List with Photos & Quantities */}
          {order.items && order.items.length > 0 ? (
            <div className="space-y-3">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between gap-4 bg-secondary/20 p-2.5 rounded-xl border border-border/40">
                  <div className="flex items-center gap-3.5 min-w-0">
                    <img
                      src={item.image || "/plantpicture/p1/5.webp"}
                      alt={item.name}
                      className="h-14 w-12 object-cover rounded-lg border border-border bg-secondary shrink-0 shadow-2xs"
                    />
                    <div className="min-w-0">
                      <h4 className="font-semibold text-xs text-foreground line-clamp-1 sm:line-clamp-2">
                        {item.name}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-[11px]">
                        <span className="bg-secondary px-2 py-0.5 rounded-md font-bold text-emerald-800 dark:text-emerald-300 border border-border/60">
                          Qty: {item.qty}
                        </span>
                        <span className="text-muted-foreground">×</span>
                        <span className="font-medium text-muted-foreground tabular-nums">
                          {formatPrice(item.price)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    <span className="font-display font-bold text-sm text-foreground tabular-nums">
                      {formatPrice((item.qty || 1) * item.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-foreground font-medium">{order.itemsSummary}</p>
          )}

          {/* Order Footer */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-muted-foreground pt-2 border-t mt-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>Payment: <strong className="text-foreground uppercase">{order.paymentMethod}</strong></span>
              {order.shippingAddress && (
                <span className="truncate max-w-xs">
                  Ship to: {order.shippingAddress.fullName || order.shippingAddress.city}
                </span>
              )}
            </div>

            <a
              href={`https://wa.me/919344580982?text=${encodeURIComponent(
                `🌸 Aster Decorz Order Support\nOrder Reference: ${order.id}\nTotal: ₹${order.total}\nStatus: ${order.status}\nHi, I would like an update on my order.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 self-start sm:self-auto text-[11px] font-semibold text-[#25D366] hover:text-[#20ba5a] hover:underline"
            >
              <MessageCircle className="h-3.5 w-3.5 fill-[#25D366]" />
              Need Help? Chat on WhatsApp
            </a>
          </div>
        </li>
      ))}
    </ul>
  );
}
